import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Users, 
  Wallet, 
  Gamepad2, 
  BarChart3, 
  ChevronRight, 
  Activity, 
  Calendar,
  Trophy,
  Target,
  Settings,
  Plus,
  DollarSign,
  Clock
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useBetting } from '@/hooks/useBetting';
import { ROUTE_PATHS, UserTier, formatCurrency, MatchStatus, BetStatus } from '@/lib/index';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/**
 * Complete Football Betting Dashboard
 * Full-featured dashboard with Layout, statistics, and role-based actions
 */
const Dashboard: React.FC = () => {
  const {
    user,
    isCompany,
    isAgent,
    isPlayer,
    effectiveStatus
  } = useAuth();
  
  const {
    matches,
    bets,
    users
  } = useBetting();

  console.log('Full Dashboard rendering with user:', user);

  // Calculate comprehensive statistics
  const stats = useMemo(() => {
    const activeMatches = matches.filter(m => 
      m.status === MatchStatus.UPCOMING || m.status === MatchStatus.LIVE
    ).length;
    
    const userBets = bets.filter(b => b.userId === user?.id);
    const pendingBetsCount = userBets.filter(b => b.status === BetStatus.PENDING).length;
    const wonBetsCount = userBets.filter(b => b.status === BetStatus.WON).length;
    
    const totalBetVolume = bets.reduce((sum, bet) => sum + bet.amount, 0);
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.status === 'ACTIVE').length;
    
    return {
      activeMatches,
      pendingBetsCount,
      wonBetsCount,
      totalBetVolume,
      totalUsers,
      activeUsers,
      userBalance: user?.balance || 0
    };
  }, [matches, bets, users, user]);

  // Role-based quick actions
  const quickActions = useMemo(() => {
    const actions = [
      {
        title: 'Place Bets',
        desc: 'Bet on upcoming football matches',
        icon: Gamepad2,
        path: ROUTE_PATHS.MATCHES,
        color: 'bg-primary',
        textColor: 'text-primary',
        visible: true
      },
      {
        title: 'View Reports',
        desc: 'Analyze performance and statistics',
        icon: BarChart3,
        path: ROUTE_PATHS.REPORTS,
        color: 'bg-blue-500',
        textColor: 'text-blue-500',
        visible: true
      },
      {
        title: 'Wallet Management',
        desc: 'Manage balance and transactions',
        icon: Wallet,
        path: ROUTE_PATHS.WALLET,
        color: 'bg-green-500',
        textColor: 'text-green-500',
        visible: true
      },
      {
        title: 'User Management',
        desc: 'Manage accounts and permissions',
        icon: Users,
        path: ROUTE_PATHS.USERS,
        color: 'bg-indigo-500',
        textColor: 'text-indigo-500',
        visible: isCompany() || isAgent()
      },
      {
        title: 'Match Management',
        desc: 'Add and manage football matches',
        icon: Settings,
        path: ROUTE_PATHS.MATCH_MANAGEMENT,
        color: 'bg-orange-500',
        textColor: 'text-orange-500',
        visible: isCompany()
      },
      {
        title: 'Match Results',
        desc: 'Enter results and settle bets',
        icon: Trophy,
        path: ROUTE_PATHS.MATCH_RESULTS,
        color: 'bg-red-500',
        textColor: 'text-red-500',
        visible: isCompany()
      }
    ];
    
    return actions.filter(a => a.visible);
  }, [isCompany, isAgent]);

  // Recent matches for display
  const recentMatches = useMemo(() => {
    return matches
      .filter(m => m.status === MatchStatus.UPCOMING || m.status === MatchStatus.LIVE)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, 4);
  }, [matches]);

  // Recent user bets
  const recentBets = useMemo(() => {
    return bets
      .filter(b => b.userId === user?.id)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 3);
  }, [bets, user?.id]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Authentication Required</h1>
          <p className="text-muted-foreground">Please log in to access the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 pb-12">
        {/* Welcome Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Welcome back, {user.username}! 👋
            </h1>
            <p className="text-muted-foreground mt-1">
              {isCompany() ? 'Company Dashboard - Full System Control' : 
               isAgent() ? 'Agent Dashboard - Manage Your Network' : 
               'Player Dashboard - Place Your Bets'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-3 py-1">
              {user.tier}
            </Badge>
            <Badge variant={effectiveStatus === 'ACTIVE' ? 'default' : 'destructive'}>
              {effectiveStatus}
            </Badge>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Available Credit" 
            value={formatCurrency(user.creditLimit || 0)} 
            icon={Wallet} 
            trend={`Credit Limit: ${formatCurrency(user.creditLimit || 0)}`} 
            color="text-green-600" 
          />
          <StatCard 
            title="Active Matches" 
            value={stats.activeMatches.toString()} 
            icon={Activity} 
            trend="Upcoming & Live" 
            color="text-blue-600" 
          />
          <StatCard 
            title={isPlayer() ? "My Pending Bets" : "Total Users"} 
            value={isPlayer() ? stats.pendingBetsCount.toString() : stats.totalUsers.toString()} 
            icon={isPlayer() ? Target : Users} 
            trend={isPlayer() ? "Awaiting Results" : "System Users"} 
            color="text-purple-600" 
          />
          <StatCard 
            title={isPlayer() ? "Won Bets" : "Bet Volume"} 
            value={isPlayer() ? stats.wonBetsCount.toString() : formatCurrency(stats.totalBetVolume)} 
            icon={isPlayer() ? Trophy : DollarSign} 
            trend={isPlayer() ? "Successful Bets" : "Total Volume"} 
            color="text-orange-600" 
          />
        </div>

        {/* Quick Actions */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Quick Actions</h2>
            <div className="text-sm text-muted-foreground">
              {quickActions.length} available actions
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions.map((action, idx) => (
              <motion.div
                key={action.title}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link 
                  to={action.path} 
                  className="group block p-6 bg-card border border-border rounded-xl hover:border-primary/50 hover:shadow-lg transition-all duration-200"
                >
                  <div className={`h-12 w-12 rounded-xl ${action.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                    <action.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{action.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{action.desc}</p>
                  <div className={`flex items-center ${action.textColor} font-medium text-sm group-hover:gap-2 transition-all`}>
                    Launch <ChevronRight className="h-4 w-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Matches */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Upcoming Matches
              </CardTitle>
              <Link to={ROUTE_PATHS.MATCHES}>
                <Button variant="ghost" size="sm">
                  View All
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              {recentMatches.length > 0 ? (
                recentMatches.map((match) => (
                  <div key={match.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[80px]">
                        <p className="font-semibold text-sm">{match.homeTeam}</p>
                        <p className="text-xs text-muted-foreground">Home</p>
                      </div>
                      <div className="px-3 py-1 bg-background rounded-full text-xs font-bold">
                        VS
                      </div>
                      <div className="text-center min-w-[80px]">
                        <p className="font-semibold text-sm">{match.awayTeam}</p>
                        <p className="text-xs text-muted-foreground">Away</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant={match.status === MatchStatus.LIVE ? 'destructive' : 'secondary'} className="mb-1">
                        {match.status}
                      </Badge>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(match.startTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="mb-2">No upcoming matches</p>
                  {isCompany() && (
                    <Link to={ROUTE_PATHS.MATCH_MANAGEMENT}>
                      <Button variant="outline" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Match
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* System Overview / Recent Bets */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                {isPlayer() ? 'My Recent Bets' : 'System Overview'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isPlayer() ? (
                // Player: Show recent bets
                recentBets.length > 0 ? (
                  recentBets.map((bet) => {
                    const match = matches.find(m => m.id === bet.matchId);
                    return (
                      <div key={bet.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-semibold text-sm">
                            {match ? `${match.homeTeam} vs ${match.awayTeam}` : 'Match'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {bet.selection.toUpperCase()} • {formatCurrency(bet.amount)}
                          </p>
                        </div>
                        <Badge variant={
                          bet.status === BetStatus.WON ? 'default' :
                          bet.status === BetStatus.LOST ? 'destructive' : 'secondary'
                        }>
                          {bet.status}
                        </Badge>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Target className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No recent bets</p>
                  </div>
                )
              ) : (
                // Company/Agent: Show system overview
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-primary">{stats.totalUsers}</p>
                      <p className="text-xs text-muted-foreground">Total Users</p>
                    </div>
                    <div className="text-center p-3 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{stats.activeUsers}</p>
                      <p className="text-xs text-muted-foreground">Active Users</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Bet Volume</span>
                      <span className="font-semibold">{formatCurrency(stats.totalBetVolume)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Your Role</span>
                      <Badge variant="outline">{user.tier}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Ticket Price</span>
                      <span className="font-semibold">{user.ticketPrice?.toFixed(2) || '1.00'}</span>
                    </div>
                    {(isCompany() || isAgent()) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Fight Percentage</span>
                        <span className="font-semibold">{user.fightPercentage || 0}%</span>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t space-y-2">
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      System Status: Online
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Database: PostgreSQL Connected
                    </div>
                    <div className="flex items-center gap-2 text-sm text-purple-600">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Security: Edge Functions Active
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

/**
 * Statistics Card Component
 */
interface StatCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  trend: string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, trend, color }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="hover:shadow-lg transition-shadow duration-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className={`text-2xl font-bold ${color} mt-1`}>{value}</p>
              <p className="text-xs text-muted-foreground mt-1">{trend}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <Icon className={`w-6 h-6 ${color}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default Dashboard;